
import React from 'react';
import { Outlet } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarExpandToggle,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  SidebarSection
} from '@/components/ui/sidebar';
import { useAuth } from '@/context/AuthContext';
import { 
  CalendarClock, 
  User, 
  Users, 
  Settings, 
  LayoutDashboard, 
  LogOut 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const DashboardLayout: React.FC = () => {
  const { logout, user } = useAuth();

  return (
    <TooltipProvider>
      <SidebarProvider>
        <div className="flex w-full min-h-screen">
          <Sidebar>
            <SidebarHeader>
              <div className="flex items-center overflow-hidden">
                <span className="font-bold text-lg overflow-hidden whitespace-nowrap">
                  Medical Admin
                </span>
              </div>
            </SidebarHeader>
            
            <SidebarContent>
              <SidebarSection title="Main">
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild variant={window.location.pathname === "/dashboard" ? "active" : "default"}>
                      <Link to="/dashboard">
                        <LayoutDashboard className="h-5 w-5" />
                        <span>Dashboard</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild variant={window.location.pathname.includes("/dashboard/appointments") ? "active" : "default"}>
                      <Link to="/dashboard/appointments">
                        <CalendarClock className="h-5 w-5" />
                        <span>Appointments</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild variant={window.location.pathname.includes("/dashboard/doctors") ? "active" : "default"}>
                      <Link to="/dashboard/doctors">
                        <User className="h-5 w-5" />
                        <span>Doctors</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild variant={window.location.pathname.includes("/dashboard/patients") ? "active" : "default"}>
                      <Link to="/dashboard/patients">
                        <Users className="h-5 w-5" />
                        <span>Patients</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild variant={window.location.pathname.includes("/dashboard/staff") ? "active" : "default"}>
                      <Link to="/dashboard/staff">
                        <User className="h-5 w-5" />
                        <span>User Management</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild variant={window.location.pathname.includes("/dashboard/webhook-settings") ? "active" : "default"}>
                      <Link to="/dashboard/webhook-settings">
                        <Settings className="h-5 w-5" />
                        <span>Webhook Settings</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarSection>
            </SidebarContent>
            
            <SidebarFooter>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                    {user?.username?.charAt(0).toUpperCase()}
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-sm font-medium truncate">{user?.username}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                  </div>
                </div>
                
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={logout}>
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Logout</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              
              <SidebarExpandToggle />
            </SidebarFooter>
          </Sidebar>
          
          <div className="flex-1 ml-64 transition-all duration-300">
            <div className="p-4">
              <Outlet />
            </div>
          </div>
        </div>
      </SidebarProvider>
    </TooltipProvider>
  );
};

export default DashboardLayout;
